import { useState } from "react";

import { localPostInfo } from "pages/[tenant]/g/utils/localPostInfo";

import api from "shared/infra/services/api";
import { useAuthContext } from "shared/utils/hooks/useAuthContext";

import { captureException, captureMessage } from "@sentry/nextjs";

import { Comment, IGroup, Post } from "../@types";
import useGroupContext from "./useGroupContext";

interface ICommentsObject {
  replys: Comment[];
  comments: Comment[];
}

export const useFetchGroups = () => {
  const {
    group,
    setSubscriptions,
    setUserSituation,
    userSituationRef,
    setModerators,
    setUserLessonDetails,
    setPosts,
    setGroup,
  } = useGroupContext();
  const { loggedUser } = useAuthContext();
  const [isfetchingModerators, setIsFetchingModerators] = useState(false);
  const [isLastPage, setIslastPage] = useState(false);
  const [isLoading, setIsloading] = useState(false);

  const scroolToCreatedPost = async ({ postId }) => {
    const el = document.getElementById(`feed__posts_${postId}`);

    el?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });
  };

  function replaceLocalPostWithCreatedPost(newPost: Post) {
    setPosts((prev) =>
      prev.map((post) => (post.id === ("local" as any) ? newPost : post)),
    );
  }
  interface ICreatePost {
    group: IGroup;
    content: string;
  }
  async function createPost({ group, content }: ICreatePost) {
    try {
      setIsloading(true);
      const response = await api.post("group-posts", {
        group_id: group?.id,
        content,
      });
      const newPost: Post = response.data.data;

      setPosts((prev) => [newPost, ...prev]);

      setTimeout(() => scroolToCreatedPost({ postId: newPost?.id }), 100);
    } catch (error) {
      captureMessage("Error ~ useFetchGroups::createPost");
      console.log("Error ~ useFetchGroups::createPost", error);
      captureException(error);
    } finally {
      setIsloading(false);
    }
  }

  async function deletePost({ postId }) {
    try {
      setIsloading(true);

      await api.delete(`group-posts/${postId}`);
      setPosts((prev) => prev.filter((post) => post.id !== postId));
    } catch (error) {
      captureMessage("Error ~ useFetchGroups::deletePost");
      console.log("Error ~ useFetchGroups::deletePost", error);
      captureException(error);
    } finally {
      setIsloading(false);
    }
  }

  const insertLocalPostInFeed = (groupId: number, content: string) => {
    setPosts((prev) => [
      {
        ...localPostInfo,
        author: {
          display_name: loggedUser?.display_name,
          username: loggedUser?.username,
          id: loggedUser?.id,
        },
        content: content,
        group_id: groupId,
      } as Post,
      ...prev,
    ]);
  };

  const updatePostContent = (postId: number, content: string) => {
    setPosts((prev) =>
      prev.map((prevPost) =>
        prevPost.id === postId ? { ...prevPost, content: content } : prevPost,
      ),
    );
  };

  async function updatePreviousPosts() {
    try {
      const postsLength = document.querySelectorAll("#feed__posts").length;

      const params = {
        "order[0][index]": "created_at",
        "order[0][value]": "desc",
        perPage: postsLength + 1,
      };

      await api.get(`groups/${group?.slug}/posts`, { params });
    } catch (error) {
      captureMessage("Error ~ useFetchGroups::updatePreviousPosts");
      console.log("Error ~ useFetchGroups::updatePreviousPosts", error);
      captureException(error);
    }
  }

  async function getComments({ id, total_comments }: Post) {
    if (!id) return { comments: [] };

    try {
      const response = await api.get(
        `group-posts/${id}/comments?perPage=${total_comments}`,
      );
      const commentsFromApi = response?.data?.data as Comment[];
      const commentsLength = commentsFromApi?.length;

      const commentsWasFounded = commentsFromApi?.length > 0;
      if (!commentsWasFounded) return { comments: [] };

      const { comments, replys } = commentsFromApi.reduce(
        (acc: ICommentsObject, comment) => {
          const commentIsReply = comment.parent_id;

          return commentIsReply
            ? { ...acc, replys: [...acc.replys, comment] }
            : { ...acc, comments: [...acc.comments, comment] };
        },
        {
          replys: [],
          comments: [],
        },
      );

      const commentsWithReplysWithCommenterSetted = comments.map((comment) => ({
        ...comment,
        children: comment.children.map((child) =>
          child.parent_id === comment.id
            ? replys.find((reply) => reply.id === child.id)
            : child,
        ),
      }));

      return {
        comments: commentsWithReplysWithCommenterSetted,
        commentsLength,
      };
    } catch (error) {
      console.log(error);
    }
  }

  async function getFullGroupData(group: IGroup) {
    try {
      setGroup(group);

      if (group) {
        const response = await api.get(`groups/${group?.slug}`);

        const group_data = response.data.data;

        setGroup(group_data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function getSubscriptions({ id }) {
    try {
      const response = await api.get(`/groups/${id}/subscription-requests`);
      setSubscriptions(response.data.data);
    } catch (error) {
      return null;
    }
  }

  async function getModerators({ id }) {
    try {
      setIsFetchingModerators(true);
      const response = await api.get(`/groups/${id}/moderators`);
      setModerators(response.data.data);
    } catch (error) {
      return null;
    } finally {
      setIsFetchingModerators(false);
    }
  }

  async function getUserSituation({ id, userId }) {
    try {
      const response = await api.get(`/groups/${id}/individual/${userId}`);
      const userSituation = response.data.data;

      setUserSituation(userSituation);
      userSituationRef.current = userSituation;
    } catch (error) {
      captureException(error);
      throw new Error(error);
    }
  }

  async function getUserLessonDetails({ lessonSlug, userId }) {
    try {
      const response = await api.get(
        `group-lessons/${lessonSlug}/user/${userId}`,
      );
      setUserLessonDetails(response.data.data);
    } catch (error) {
      captureException(error);
      throw new Error(error);
    }
  }

  return {
    updatePreviousPosts,
    getComments,
    getModerators,
    getUserSituation,
    getUserLessonDetails,
    getSubscriptions,
    getFullGroupData,
    replaceLocalPostWithCreatedPost,

    createPost,
    deletePost,

    insertLocalPostInFeed,
    updatePostContent,
    isfetchingModerators,
    isLoading,
    isLastPage,
  };
};
